import cx from 'classix';
import React, { MutableRefObject, ReactNode, useRef } from 'react';
import { AriaButtonOptions, useButton } from 'react-aria';
import { Icon, IconProps } from '../atoms';
import { Link } from 'wouter';

export type ButtonVariants =
  | 'primary'
  | 'secondary-color'
  | 'tertiary-color'
  | 'secondary-gray'
  | 'tertiary-gray'
  | 'accent'
  | 'primary-danger'
  | 'secondary-danger'
  | 'tertiary-danger'
  | 'link-color';
type ButtonSizes = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type StandardButtonProps = {
  variant: ButtonVariants;
  size: ButtonSizes;
  children: ReactNode;
  leadIcon?: IconProps['type'];
  leadIconClassNames?: string;
  tailIcon?: IconProps['type'];
  tailIconClassNames?: string;
  buttonRef?: MutableRefObject<HTMLButtonElement | HTMLAnchorElement | null>;
  additionalClassnames?: string;
  dataTestId?: string;
  isDisabled?: boolean;
};

type IconButtonProps = {
  variant: ButtonVariants;
  size: ButtonSizes;
  icon: IconProps['type'];
};

const ATag = React.forwardRef<
  HTMLAnchorElement,
  React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>
>((props, ref) => {
  return <a {...props} ref={ref} />;
});

const ButtonTag = React.forwardRef<
  HTMLButtonElement,
  React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
>((props, ref) => {
  return <button {...props} ref={ref} />;
});

const BaseButton = (
  props: StandardButtonProps &
    AriaButtonOptions<'button'> & {
      grouped?: boolean;
      groupedFirstItem?: boolean;
      groupedLastItem?: boolean;
      href?: string;
    },
) => {
  const {
    variant,
    size,
    children,
    grouped,
    groupedFirstItem,
    additionalClassnames = '',
    groupedLastItem,
    dataTestId,
  } = props;

  const hasHref = props?.href?.length;

  const buttonSize = variant !== 'link-color' ? size : `link-${size}`;
  const ref = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);

  let ButtonElement = hasHref ? ATag : ButtonTag;
  let href = props.href;

  if (ButtonElement === ATag && href?.startsWith('/')) {
    href = href.replace('/platform', '~/platform');
    ButtonElement = Link as unknown as typeof ATag; // Cast to Link for wouter compatibility
  }

  const { buttonProps } = useButton(
    {
      elementType: hasHref ? 'a' : 'button',
      ...props,
      href: href, // Overrides props.href to the corrected value for in-SPA navigation
    },
    ref,
  );

  return (
    <ButtonElement
      ref={(newRef: HTMLButtonElement | HTMLAnchorElement | null) => {
        ref.current = newRef;
        if (props.buttonRef) {
          props.buttonRef.current = newRef;
        }
      }}
      data-testid={dataTestId}
      className={cx(
        // Style and state classes
        'max-w-full font-sans font-semibold focus:outline-none flex justify-center items-center disabled:text-gray-300',
        !grouped && 'rounded-lg',
        grouped && 'border-t border-r border-b',
        groupedFirstItem && 'rounded-s-lg border-l',
        groupedLastItem && 'rounded-e-lg',
        variant === 'primary' &&
          'bg-brand-500 border-brand-600 hover:bg-brand-600 active:bg-brand-600 active:border-brand-600',
        variant === 'secondary-color' &&
          'bg-secondary-brand-25 border-gray-100 hover:bg-secondary-brand-50 hover:border-secondary-brand-50',
        variant === 'tertiary-color' &&
          'hover:bg-secondary-brand-25 focus:bg-secondary-brand-25 active:shadow-none',
        variant === 'secondary-gray' &&
          'bg-white border-gray-200 hover:text-gray-600 active:bg-gray-50 active:text-gray-600 disabled:border-gray-100',
        variant === 'tertiary-gray' && 'focus:bg-gray-50 active:bg-gray-100 active:shadow-none',
        variant === 'accent' &&
          'bg-accent-500 border-accent-500 hover:bg-accent-600 hover:border-accent-600 focus:border-accent-600 focus:ring-gray-secondary active:bg-accent-600 active:border-accent-600',
        variant === 'primary-danger' &&
          'bg-error-500 border-error-500 hover:bg-error-600 hover:border-error-600 hover:shadow-xs focus:border-error-100 active:bg-error-600 active:border-error-600',
        variant === 'secondary-danger' &&
          'border-error-100 hover:shadow-xs disabled:border-gray-100 bg-white',
        variant === 'tertiary-danger' &&
          'focus:bg-white focus:border focus:border-error-100 active:border active:border-error-100 disabled:border-none',
        variant === 'link-color' &&
          'text-brand-500 hover:text-brand-600 focus-visible:border focus:border-brand-500 active:text-brand-600 active:border-none active:underline disabled:no-underline',
        (variant === 'primary' || variant === 'primary-danger') && 'text-white',
        (variant === 'primary' || variant === 'secondary-color') &&
          'focus:border-secondary-brand-50',
        (variant === 'secondary-color' || variant === 'tertiary-color') &&
          'text-brand-500 active:bg-secondary-brand-50',
        (variant === 'secondary-gray' || variant === 'tertiary-gray') &&
          'hover:bg-gray-50 focus:ring-gray-shadow-xs',
        (variant === 'secondary-danger' || variant === 'tertiary-danger') &&
          'text-error-600 hover:bg-error-25 active:bg-error-25',
        variant.includes('danger') && 'focus:ring-error-shadow-xs active:ring-error',
        !['tertiary-color', 'tertiary-gray', 'tertiary-danger', 'link-color'].includes(variant) &&
          !grouped &&
          'border',
        ['secondary-gray', 'tertiary-gray', 'accent'].includes(variant) && 'text-gray-500',
        ['primary', 'secondary-color', 'secondary-gray', 'accent', 'primary-danger'].includes(
          variant,
        ) &&
          !grouped &&
          'shadow-xs',
        ['primary', 'secondary-color', 'tertiary-color'].includes(variant) &&
          'focus:ring-brand-shadow-xs',
        ['primary', 'secondary-color', 'accent', 'primary-danger'].includes(variant) ?
          'disabled:bg-gray-100 disabled:border-gray-100'
        : 'disabled:bg-white',
        // Size and layout classes
        variant === 'link-color' && 'p-0.5',
        buttonSize === 'sm' && `${children === '' ? 'w-9' : 'min-w-9'} h-9 px-3 py-2`,
        buttonSize === 'md' && `${children === '' ? 'w-10' : 'min-w-10'} h-10 px-3.5 py-2.5`,
        buttonSize === 'lg' && `${children === '' ? 'w-11' : 'min-w-11'} h-11 px-4 py-2.5`,
        buttonSize === 'xl' && `${children === '' ? 'w-12' : 'min-w-12'} h-12 px-[18px] py-4`,
        buttonSize === '2xl' &&
          `${children === '' ? 'w-[60px]' : 'min-w-[60px]'} h-[60px] text-lg px-[22px] py-4 gap-x-2.5`,
        (buttonSize === 'sm' || buttonSize === 'md') && 'text-sm gap-x-1',
        (buttonSize === 'lg' || buttonSize === 'xl') && 'text-base gap-x-1.5',
        (buttonSize === 'link-sm' || buttonSize === 'link-md') && 'min-w-6 h-6 gap-x-1.5',
        (buttonSize === 'link-lg' || buttonSize === 'link-xl') && 'min-w-7 h-7 gap-x-2',
        buttonSize === 'link-2xl' && 'min-w-8 h-8 gap-x-3',
        buttonSize === 'link-sm' && 'text-sm',
        buttonSize === 'link-md' && 'text-sm',
        buttonSize === 'link-lg' && 'text-base',
        buttonSize === 'link-xl' && 'text-base',
        buttonSize === 'link-2xl' && 'text-lg',
        additionalClassnames,
      )}
      {...buttonProps}
      /* Override react-aria's behavior of setting all useButton() elements as role == 'button'. See
       * https://theadminbar.com/accessibility-weekly/buttons-vs-links/ for motivation on having <a> tags give a role of type 'link'
       * even when styled as a button.
       * Note that this also has implications in our e2e tests as we often select elements via getByRole() */
      role={hasHref ? 'link' : 'button'}
      disabled={props.isDisabled}
    >
      {props.leadIcon && (
        <Icon
          additionalClassNames={props.leadIconClassNames}
          strokeWidth='1.5px'
          type={props.leadIcon}
          size={props.size === '2xl' ? '24' : '20'}
        />
      )}
      {children}
      {props.tailIcon && (
        <Icon
          additionalClassNames={props.tailIconClassNames}
          strokeWidth='1.5px'
          type={props.tailIcon}
          size={props.size === '2xl' ? '24' : '20'}
        />
      )}
    </ButtonElement>
  );
};

export const GroupedButton = (props: {
  groupedFirstItem: boolean;
  groupedLastItem: boolean;
  onPress: () => void;
  label?: string;
  icon?: IconProps['type'];
  isDisabled?: boolean;
  dataTestId?: string;
}) => {
  return (
    <BaseButton
      isDisabled={props.isDisabled}
      grouped={true}
      groupedFirstItem={props.groupedFirstItem}
      groupedLastItem={props.groupedLastItem}
      size='md'
      variant='secondary-gray'
      leadIcon={props.icon}
      onPress={props.onPress}
    >
      {props.label}
    </BaseButton>
  );
};

export const Button = (props: StandardButtonProps & AriaButtonOptions<'button'>) => {
  return <BaseButton {...props}>{props.children}</BaseButton>;
};

export const IconButton = (
  props: IconButtonProps & AriaButtonOptions<'button'> & { additionalClassnames?: string },
) => {
  return (
    <BaseButton
      children=''
      variant={props.variant}
      size={props.size}
      leadIcon={props.icon}
      onPress={props.onPress}
      isDisabled={props.isDisabled}
      additionalClassnames={props.additionalClassnames}
    />
  );
};
